import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { CheckCircle, Move, X } from 'lucide-react';
import { useFilteredNotes } from '../hooks/useNotes';
import { useCollections } from '../hooks/useCollections';
import { CollectionCard, NewNotebookTile } from '../components/CollectionCard';
import { coverFor } from '../lib/covers';
import { HIGHLIGHTERS, highlighterFor } from '../lib/paper';
import { shelfLine } from '../lib/shelf';
import { NoteCard } from '../components/NoteCard';
import { NewCollectionModal } from '../components/NewCollectionModal';
import { Doodle, EnterOnce, Highlight, Loop } from '../components/paper';
import { toaster } from '../components/ui';

const PAGE = 'mx-auto max-w-[60rem] px-5 pb-16 pt-[calc(1.5rem+var(--mobile-nav-offset))] md:px-10 md:pt-14';

const DRAG_MIME = 'application/x-pb-note';

/**
 * Library — the web twin of mobile's shelf (mobile/app/(app)/(tabs)/library/index.tsx):
 * "Your shelf" of notebooks, then the loose pages. On desktop, drag a loose page
 * onto a notebook to file it. `?tag=` narrows to one tag.
 */
export const Library: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newCollectionOpen, setNewCollectionOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const selectedTag = searchParams.get('tag') || undefined;
  const { notes: filteredNotes, allNotes, isLoading } = useFilteredNotes(selectedTag);
  const { collections, createCollection, isCreating, setNoteCollection } = useCollections();

  const collectedNoteIds = new Set(collections.flatMap((c) => c.note_ids));
  const loose = allNotes.filter((n) => !collectedNoteIds.has(n.id));

  // Drag-and-drop is for fine pointers only: HTML5 DnD is unreliable on touch.
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    const update = (): void => setIsTouch(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const dragEnabled = !isTouch && collections.length > 0;

  const [draggingNoteId, setDraggingNoteId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  const hasActiveDrag = (e: React.DragEvent): boolean =>
    draggingNoteId !== null || Array.from(e.dataTransfer.types).includes(DRAG_MIME);

  const onDragOver = (e: React.DragEvent, collectionId: number): void => {
    if (!hasActiveDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetId !== collectionId) setDropTargetId(collectionId);
  };

  const onDragLeave = (e: React.DragEvent, collectionId: number): void => {
    // Only clear when the pointer has really left this card, not entered a child.
    if (dropTargetId !== collectionId) return;
    const related = e.relatedTarget as Node | null;
    if (related && (e.currentTarget as HTMLElement).contains(related)) return;
    setDropTargetId(null);
  };

  const onDrop = async (e: React.DragEvent, collectionId: number): Promise<void> => {
    const raw = e.dataTransfer.getData(DRAG_MIME);
    setDropTargetId(null);
    setDraggingNoteId(null);
    if (!raw) return;
    e.preventDefault();
    const noteId = Number(raw);
    if (!Number.isFinite(noteId)) return;
    const target = collections.find((c) => c.id === collectionId);
    try {
      await setNoteCollection({ noteId, collectionId });
      toaster.success({ title: 'Filed', description: `Moved to "${target?.name ?? 'notebook'}".` });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toaster.error({ title: "Couldn't file that page", description: typeof detail === 'string' ? detail : 'Please try again.' });
    }
  };

  const handleCreateCollection = async (values: { name: string; description: string | null }): Promise<void> => {
    setCreateError(null);
    try {
      const created = await createCollection({ name: values.name, description: values.description });
      setNewCollectionOpen(false);
      toaster.success({ title: 'Notebook started', description: `"${created.name}" is ready for pages.` });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setCreateError(typeof detail === 'string' ? detail : 'Could not create the notebook. Try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" color="var(--accent)" />
      </div>
    );
  }

  /* ── Tag filter ── */
  if (selectedTag) {
    return (
      <div className={PAGE}>
        <p className="pb-eyebrow m-0">Library</p>
        <h1 className="pb-heading-1 m-0 mt-1">
          <Highlight tone={highlighterFor(selectedTag)}>#{selectedTag}</Highlight>
        </h1>
        <p className="m-0 mt-2 font-body text-sm text-on-surface-variant">
          {filteredNotes.length} {filteredNotes.length === 1 ? 'page' : 'pages'} tagged
        </p>
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-full border-none bg-surface-container-high px-3 py-1 font-body text-[0.6875rem] text-on-surface-variant hover:text-on-surface"
        >
          <X size={13} /> Clear filter
        </button>

        {filteredNotes.length === 0 ? (
          <div className="mt-6 flex flex-col items-center gap-2 text-center">
            <Doodle kind="search" size={0.8} />
            <p className="m-0 font-body text-sm text-on-surface-variant">Nothing tagged #{selectedTag} yet.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
            {filteredNotes.map((note, i) => (
              <EnterOnce key={note.id} id={`note:${note.id}`} index={i}>
                <NoteCard note={note} />
              </EnterOnce>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Shelf ── */
  return (
    <div className={PAGE}>
      <p className="pb-eyebrow m-0">Library</p>
      <h1 className="pb-heading-1 m-0 mt-1">
        <Highlight tone={HIGHLIGHTERS.mint}>Your&nbsp;shelf</Highlight>
      </h1>
      <p className="m-0 mt-2 font-body text-sm text-on-surface-variant">{shelfLine(collections.length, loose.length)}</p>

      {collections.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-3 text-center">
          <Doodle kind="notebooks" size={0.85} />
          <p className="m-0 px-4 font-body text-sm text-on-surface-variant">
            No notebooks yet. Start one and drag loose pages into it.
          </p>
          <div className="mt-1 w-1/2 max-w-[13rem]">
            <NewNotebookTile onClick={() => setNewCollectionOpen(true)} />
          </div>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-[repeat(auto-fill,minmax(11.5rem,1fr))] md:gap-x-6 md:gap-y-7">
          {collections.map((c, i) => (
            <EnterOnce key={c.id} id={`notebook:${c.id}`} index={i}>
              <CollectionCard
                id={c.id}
                name={c.name}
                noteCount={c.note_count}
                cover={coverFor(c, i)}
                onClick={() => navigate(`/library/${c.id}`)}
                isDropTarget={dragEnabled && draggingNoteId !== null}
                isDropActive={dropTargetId === c.id}
                onDragOver={dragEnabled ? (e) => onDragOver(e, c.id) : undefined}
                onDragEnter={dragEnabled ? (e) => onDragOver(e, c.id) : undefined}
                onDragLeave={dragEnabled ? (e) => onDragLeave(e, c.id) : undefined}
                onDrop={dragEnabled ? (e) => void onDrop(e, c.id) : undefined}
              />
            </EnterOnce>
          ))}
          <NewNotebookTile onClick={() => setNewCollectionOpen(true)} />
        </div>
      )}

      <div className="mt-12 flex items-center gap-2">
        <h2 className="pb-eyebrow m-0 not-italic">Loose pages</h2>
        {loose.length > 0 && (
          <span className="relative inline-flex h-6 min-w-[30px] items-center justify-center font-body text-xs font-bold text-accent">
            {loose.length}
            <Loop width={30} height={24} seed={loose.length + 7} className="absolute inset-0" />
          </span>
        )}
      </div>
      {dragEnabled && loose.length > 0 && (
        <p className="m-0 mb-3 mt-1 flex items-center gap-1.5 font-body text-[0.6875rem] text-on-surface-variant">
          <Move size={12} /> Drag a page onto a notebook to file it.
        </p>
      )}

      {loose.length > 0 ? (
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
          {loose.map((note, i) => (
            <EnterOnce key={note.id} id={`note:${note.id}`} index={i}>
              <div
                draggable={dragEnabled}
                onDragStart={(e) => {
                  e.dataTransfer.setData(DRAG_MIME, String(note.id));
                  e.dataTransfer.effectAllowed = 'move';
                  setDraggingNoteId(note.id);
                }}
                onDragEnd={() => {
                  setDraggingNoteId(null);
                  setDropTargetId(null);
                }}
                className={`${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''} ${
                  draggingNoteId === note.id ? 'opacity-40' : ''
                }`}
              >
                <NoteCard note={note} />
              </div>
            </EnterOnce>
          ))}
        </div>
      ) : (
        allNotes.length > 0 && (
          <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-hl-mint px-3.5 py-2 font-body text-[0.8125rem] font-semibold text-hl-mint-ink">
            <CheckCircle size={18} /> All filed away. Tidy desk!
          </span>
        )
      )}

      <NewCollectionModal
        isOpen={newCollectionOpen}
        onClose={() => {
          if (isCreating) return;
          setNewCollectionOpen(false);
          setCreateError(null);
        }}
        onSubmit={handleCreateCollection}
        isSubmitting={isCreating}
        errorMessage={createError}
      />
    </div>
  );
};
